import { createTheme } from '@mui/material/styles';

// カスタムブレークポイント
const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
  },
};

// ゲーミング・ネオンカラーパレット
const palette = {
  mode: 'dark',
  primary: {
    main: '#00ffff', // シアン
    light: '#66ffff',
    dark: '#00cccc',
    contrastText: '#000000',
  },
  secondary: {
    main: '#ff00ff', // マゼンタ
    light: '#ff66ff',
    dark: '#cc00cc',
    contrastText: '#000000',
  },
  success: {
    main: '#00ff00', // ライムグリーン
    light: '#66ff66',
    dark: '#00cc00',
    contrastText: '#000000',
  },
  error: {
    main: '#ff0066', // ホットピンク
    light: '#ff3388',
    dark: '#cc0052',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#ffaa00', // オレンジ
    light: '#ffbb33',
    dark: '#cc8800',
    contrastText: '#000000',
  },
  info: {
    main: '#00aaff', // スカイブルー
    light: '#33bbff',
    dark: '#0088cc',
    contrastText: '#000000',
  },
  grey: {
    50: '#1a1a1a',
    100: '#262626',
    200: '#333333',
    300: '#404040',
    400: '#595959',
    500: '#737373',
    600: '#8c8c8c',
    700: '#a6a6a6',
    800: '#bfbfbf',
    900: '#d9d9d9',
  },
  background: {
    default: '#1a1a1f', // ダークネイビー
    paper: '#252530', // ダークグレー
    elevated: 'rgba(37, 37, 48, 0.95)',
  },
  text: {
    primary: '#ffffff',
    secondary: '#b3b3b3',
    disabled: '#666666',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
  action: {
    active: '#00ffff',
    hover: 'rgba(0, 255, 255, 0.08)',
    selected: 'rgba(0, 255, 255, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
  },
  neon: {
    cyan: '#00ffff',
    magenta: '#ff00ff',
    lime: '#00ff00',
    orange: '#ff6600',
    pink: '#ff0066',
    purple: '#9945ff',
    yellow: '#ffff00',
  },
};

// ゲーミングフォント設定
const typography = {
  fontFamily: '"Orbitron", "Rajdhani", "Inter", "Noto Sans JP", sans-serif',
  h1: {
    fontWeight: 900,
    letterSpacing: '0.02em',
    lineHeight: 1.1,
    textTransform: 'uppercase',
  },
  h2: {
    fontWeight: 800,
    letterSpacing: '0.02em',
    lineHeight: 1.2,
    textTransform: 'uppercase',
  },
  h3: {
    fontWeight: 700,
    letterSpacing: '0.01em',
    lineHeight: 1.3,
  },
  h4: {
    fontWeight: 700,
    letterSpacing: '0.01em',
    lineHeight: 1.3,
  },
  h5: {
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.4,
  },
  h6: {
    fontWeight: 600,
    letterSpacing: '0.01em',
    lineHeight: 1.4,
  },
  subtitle1: {
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  subtitle2: {
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.01em',
  },
  body1: {
    lineHeight: 1.6,
    letterSpacing: '0.005em',
  },
  body2: {
    lineHeight: 1.6,
    letterSpacing: '0.005em',
  },
  button: {
    textTransform: 'uppercase',
    fontWeight: 700,
    letterSpacing: '0.08em',
  },
  caption: {
    lineHeight: 1.5,
    letterSpacing: '0.02em',
  },
  overline: {
    lineHeight: 1.5,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    fontWeight: 600,
  },
};

// ネオンシャドウ
const shadows = [
  'none',
  '0 0 10px rgba(0, 255, 255, 0.3)',
  '0 0 20px rgba(0, 255, 255, 0.3)',
  '0 0 30px rgba(0, 255, 255, 0.3)',
  '0 0 40px rgba(0, 255, 255, 0.3)',
  '0 0 50px rgba(0, 255, 255, 0.3)',
  '0 0 60px rgba(0, 255, 255, 0.3)',
  '0 0 70px rgba(0, 255, 255, 0.3)',
  '0 0 80px rgba(0, 255, 255, 0.3)',
  '0 0 90px rgba(0, 255, 255, 0.3)',
  ...Array(15).fill('none'),
];

// コンポーネントのゲーミングスタイル
const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundImage: `
          radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 20%, rgba(0, 255, 0, 0.1) 0%, transparent 50%)
        `,
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" xmlns="http://www.w3.org/2000/svg"%3E%3Cdefs%3E%3Cpattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse"%3E%3Cpath d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(0,255,255,0.03)" stroke-width="1"/%3E%3C/pattern%3E%3C/defs%3E%3Crect width="100%25" height="100%25" fill="url(%23grid)" /%3E%3C/svg%3E")',
          pointerEvents: 'none',
          zIndex: 0,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        padding: '10px 24px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        border: '2px solid transparent',
        fontWeight: 700,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          transition: 'left 0.5s ease',
        },
        '&:hover::before': {
          left: '100%',
        },
        '&:hover': {
          transform: 'translateY(-2px)',
        },
      },
      contained: {
        background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
        boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
        border: '2px solid #00ffff',
        color: '#000000',
        '&:hover': {
          background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
          boxShadow: '0 0 30px rgba(0, 255, 255, 0.8)',
          border: '2px solid #66ffff',
        },
      },
      outlined: {
        borderWidth: 2,
        borderColor: '#00ffff',
        color: '#00ffff',
        background: 'rgba(0, 255, 255, 0.05)',
        '&:hover': {
          borderWidth: 2,
          borderColor: '#66ffff',
          background: 'rgba(0, 255, 255, 0.1)',
          boxShadow: '0 0 15px rgba(0, 255, 255, 0.5)',
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.1) rotate(5deg)',
          filter: 'drop-shadow(0 0 10px currentColor)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: 'rgba(20, 20, 20, 0.9)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 255, 255, 0.2)',
      },
      elevation1: {
        boxShadow: '0 0 20px rgba(0, 255, 255, 0.2)',
      },
      elevation2: {
        boxShadow: '0 0 30px rgba(0, 255, 255, 0.3)',
      },
      elevation3: {
        boxShadow: '0 0 40px rgba(0, 255, 255, 0.4)',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 600,
        transition: 'all 0.3s ease',
        border: '1px solid currentColor',
        background: 'rgba(0, 255, 255, 0.1)',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: '0 0 15px currentColor',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          transition: 'all 0.3s ease',
          '& fieldset': {
            borderColor: 'rgba(0, 255, 255, 0.3)',
            borderWidth: 2,
          },
          '&:hover fieldset': {
            borderColor: 'rgba(0, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#00ffff',
            boxShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
          },
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 0,
        border: '2px solid rgba(0, 255, 255, 0.3)',
        boxShadow: '0 0 50px rgba(0, 255, 255, 0.5)',
        background: 'rgba(10, 10, 10, 0.95)',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 0,
        border: '1px solid currentColor',
        background: 'rgba(0, 0, 0, 0.8)',
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        fontSize: '0.75rem',
        borderRadius: 0,
        border: '1px solid rgba(0, 255, 255, 0.3)',
        boxShadow: '0 0 10px rgba(0, 255, 255, 0.3)',
      },
    },
  },
};

// テーマの作成
export const theme = createTheme({
  breakpoints,
  palette,
  typography,
  shadows,
  components,
  shape: {
    borderRadius: 0,
  },
  spacing: 8,
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
});

// ライトモード（将来の拡張用）
export const lightTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    mode: 'light',
    background: {
      default: '#f0f0f0',
      paper: '#ffffff',
      elevated: 'rgba(255, 255, 255, 0.95)',
    },
    text: {
      primary: '#0a0a0a',
      secondary: '#404040',
      disabled: '#999999',
    },
  },
});
